import React, { useState, useEffect } from "react";
import { cn } from "../../lib/utils.js";
import {
  PresetAvatarSvg,
  isPresetAvatarId,
  resolveOrgUniquePreset,
} from "./presetAvatars.jsx";

const SIZE_CLASSES = {
  xs: "h-4 w-4",
  sm: "h-5 w-5",
  md: "h-8 w-8",
  lg: "h-10 w-10",
  xl: "h-16 w-16",
};

/**
 * Universal UserAvatar component that guarantees every user has either a real profile photo
 * or one of the 20 bespoke Homy preset avatars (deterministically resolved per organization).
 */
export function UserAvatar({
  user,
  src,
  name,
  email,
  userId,
  orgId = "default-org",
  usedPresetIds,
  size = "md",
  theme,
  mode,
  className,
  title,
  "data-testid": dataTestId,
  ...props
}) {
  const rawSrc =
    src ??
    user?.pictureUrl ??
    user?.picture_url ??
    user?.avatarUrl ??
    user?.avatar ??
    null;

  const resolvedName =
    name ??
    user?.name ??
    ([user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      [user?.first_name, user?.last_name].filter(Boolean).join(" ")) ??
    "";

  const resolvedKey =
    userId ??
    user?.id ??
    user?.userId ??
    email ??
    user?.email ??
    resolvedName ??
    "homy-user";

  const resolvedOrgId =
    user?.organizationId ?? user?.organization_id ?? orgId ?? "default-org";

  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [rawSrc]);

  const isPreset = isPresetAvatarId(rawSrc);
  const hasCustomUrl = Boolean(rawSrc && !isPreset && !imgError);

  const fallbackPresetId = isPreset
    ? rawSrc
    : resolveOrgUniquePreset(resolvedKey, resolvedOrgId, usedPresetIds);

  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;

  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 overflow-hidden rounded-full ring-1 ring-border/80 group-hover:ring-primary/40 dark:ring-white/20 transition-all select-none",
        sizeClass,
        className
      )}
      title={title || resolvedName || undefined}
      data-testid={dataTestId}
      data-avatar-preset={hasCustomUrl ? undefined : fallbackPresetId}
      {...props}
    >
      {hasCustomUrl ? (
        <img
          src={rawSrc}
          alt={resolvedName || "User avatar"}
          className="aspect-square h-full w-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <PresetAvatarSvg
          presetId={fallbackPresetId}
          theme={theme}
          mode={mode}
          title={resolvedName || undefined}
          className="h-full w-full"
        />
      )}
    </span>
  );
}
